import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Checkbox,
  Card,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Link,
  Tooltip,
} from '@mui/material';
import { Search, MoreVertical, Filter, Download, ExternalLink } from 'lucide-react';
import type { ReviewData } from '../../types/review';

interface ReviewsTableProps {
  reviews: ReviewData[];
  onExportData: () => void;
}

const ReviewsTable = ({ reviews, onExportData }: ReviewsTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentReviewId, setCurrentReviewId] = useState<string | null>(null);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedReviews(filteredReviews.map((review) => review.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const handleSelectOne = (reviewId: string) => {
    setSelectedReviews((prev) =>
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, reviewId: string) => {
    setAnchorEl(event.currentTarget);
    setCurrentReviewId(reviewId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentReviewId(null);
  };

  const getStatusColor = (status: ReviewData['reward_claim_status']) => {
    switch (status) {
      case 'paid':
        return { bg: '#E8F5E9', color: '#2E7D32', label: 'Paid' };
      case 'approved':
        return { bg: '#E3F2FD', color: '#1976D2', label: 'Approved' };
      case 'pending':
        return { bg: '#FFF8E1', color: '#F57C00', label: 'Pending' };
      case 'not_claimed':
        return { bg: '#F5F5F5', color: '#757575', label: 'Not Claimed' };
      case 'rejected':
        return { bg: '#FFEBEE', color: '#C62828', label: 'Rejected' };
      default:
        return { bg: '#F5F5F5', color: '#757575', label: status };
    }
  };

  const filteredReviews = reviews.filter((review) => {
    const query = searchQuery.toLowerCase();
    return (
      review.customer_name.toLowerCase().includes(query) ||
      review.customer_email?.toLowerCase().includes(query) ||
      review.customer_phone?.toLowerCase().includes(query) ||
      review.campaign_name.toLowerCase().includes(query) ||
      review.platform.toLowerCase().includes(query)
    );
  });

  const formatRewardDisplay = (review: ReviewData): string => {
    switch (review.reward_type) {
      case 'coupon':
        return `Coupon: ${review.reward_value || 'N/A'}`;
      case 'cash':
        return `Cash: ${review.reward_amount || 'N/A'}`;
      case 'none':
        return 'No Reward';
      default:
        return 'N/A';
    }
  };

  return (
    <Card
      sx={{
        border: '1px solid #EAECEF',
        borderRadius: 3,
        boxShadow: 'none',
      }}
    >
      {/* Table Header Section */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #EAECEF',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Customer Reviews
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              width: { xs: '100%', sm: 256 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#F9FAFB',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#9CA3AF" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Filter size={18} />}
            sx={{
              minWidth: 'auto',
              px: 2,
              borderColor: '#E5E7EB',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download size={18} />}
            onClick={onExportData}
            sx={{
              minWidth: 'auto',
              px: 2,
              borderColor: '#E5E7EB',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: '#F9FAFB' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedReviews.length === filteredReviews.length && filteredReviews.length > 0}
                  indeterminate={
                    selectedReviews.length > 0 && selectedReviews.length < filteredReviews.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Contact Details
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Platform
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Reward
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Survey Completed
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Review Posted
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Rewards Claimed
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Review
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? 'No reviews found matching your search.' : 'No reviews available yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredReviews.map((review) => {
                const statusStyle = getStatusColor(review.reward_claim_status);
                return (
                  <TableRow
                    key={review.id}
                    hover
                    selected={selectedReviews.includes(review.id)}
                    sx={{
                      borderBottom: '1px solid #EAECEF',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedReviews.includes(review.id)}
                        onChange={() => handleSelectOne(review.id)}
                      />
                    </TableCell>
                    
                    {/* Contact Details Column */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}>
                          {review.campaign_name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5 }}>
                          {review.customer_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', display: 'block' }}>
                          {review.customer_email || 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', display: 'block' }}>
                          {review.customer_phone || 'N/A'}
                        </Typography>
                        {review.posted_at && (
                          <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: 500, mt: 0.5, display: 'block' }}>
                            Posted: {new Date(review.posted_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </Typography>
                        )}
                        {review.review_url && (
                          <Link
                            href={review.review_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              fontSize: '0.75rem',
                              color: '#4A90E2',
                              textDecoration: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              width: 'fit-content',
                              mt: 0.5,
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            View Review
                            <ExternalLink size={14} />
                          </Link>
                        )}
                      </Box>
                    </TableCell>
                    
                    {/* Platform Column */}
                    <TableCell>
                      {review.platform_link ? (
                        <Link
                          href={review.platform_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: '0.875rem',
                            textTransform: 'capitalize',
                            fontWeight: 500,
                            color: '#4A90E2',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {review.platform}
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            textTransform: 'capitalize',
                            fontWeight: 500,
                          }}
                        >
                          {review.platform}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatRewardDisplay(review)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={review.survey_completed ? 'Yes' : 'No'}
                          size="small"
                          sx={{
                            bgcolor: review.survey_completed ? '#E8F5E9' : '#FFEBEE',
                            color: review.survey_completed ? '#2E7D32' : '#C62828',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24,
                          }}
                        />
                        {review.survey_completed_at && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', fontSize: '0.7rem', mt: 0.5 }}>
                            {new Date(review.survey_completed_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={review.review_posted ? 'Yes' : 'No'}
                        size="small"
                        sx={{
                          bgcolor: review.review_posted ? '#E8F5E9' : '#FFEBEE',
                          color: review.review_posted ? '#2E7D32' : '#C62828',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                      />
                      {review.posted_at && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', fontSize: '0.7rem', mt: 0.5 }}>
                          {new Date(review.posted_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusStyle.label}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                      />
                    </TableCell>
                    {/* Review Text Column - Moved to last position */}
                    <TableCell>
                      {review.review_text ? (
                        <Tooltip
                          title={review.review_text}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#1F2937',
                                color: 'white',
                                fontSize: '0.875rem',
                                maxWidth: 400,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                p: 2,
                              }
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.4,
                              cursor: 'help',
                              maxWidth: 300
                            }}
                          >
                            {review.review_text}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            color: '#9CA3AF',
                            fontStyle: 'italic'
                          }}
                        >
                          No review text
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, review.id)}
                        sx={{ color: '#6B7280' }}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>View Review</MenuItem>
        <MenuItem onClick={handleMenuClose}>Contact Customer</MenuItem>
      </Menu>
    </Card>
  );
};

export default ReviewsTable;

